import { Link } from "react-router-dom";

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div className="min-w-0">
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-700 m-0 mb-1">
          {eyebrow}
        </p>
      ) : null}
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 m-0 tracking-tight">{title}</h1>
      {subtitle ? <p className="text-sm text-slate-500 mt-1.5 mb-0 max-w-2xl">{subtitle}</p> : null}
    </div>
    {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
  </div>
);

/** Pass `to` and the whole card becomes the way into the screen that explains the number. */
export const StatCard = ({ label, value, hint, tone = "default", to }) => {
  const toneClass =
    tone === "good"
      ? "border-teal-200 bg-teal-50/70"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50/70"
      : tone === "danger"
      ? "border-rose-200 bg-rose-50/70"
      : "border-slate-200 bg-white";
  const body = (
    <>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">
        {label}
      </p>
      <p className="text-2xl font-bold text-slate-900 m-0 mt-1 tabular-nums">{value}</p>
      {hint ? <p className="text-xs text-slate-500 m-0 mt-1">{hint}</p> : null}
    </>
  );
  const className = `block rounded-2xl border px-4 py-3.5 ${toneClass}`;
  return to ? (
    <Link to={to} className={`${className} no-underline hover:border-teal-400`}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
};

export const Panel = ({ title, action, children, className = "" }) => (
  <section className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900 m-0">{title}</h2>
        {action}
      </div>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const EmptyState = ({ title, description, action }) => (
  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-10 text-center">
    <p className="text-sm font-semibold text-slate-800 m-0">{title}</p>
    {description ? <p className="text-sm text-slate-500 mt-1 mb-0">{description}</p> : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

export const StatusChip = ({ children, tone = "neutral" }) => {
  const tones = {
    neutral: "bg-slate-100 text-slate-700",
    teal: "bg-teal-100 text-teal-800",
    amber: "bg-amber-100 text-amber-900",
    rose: "bg-rose-100 text-rose-800",
    sky: "bg-sky-100 text-sky-800",
    emerald: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
};

export const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (/(paid|verified|completed|confirmed|active|accepted)/.test(s)) return "emerald";
  if (/(pending|review|requested|earned|progress|info)/.test(s)) return "amber";
  if (/(reject|cancel|need|draft)/.test(s)) return "rose";
  if (/(submitted|sent)/.test(s)) return "sky";
  return "neutral";
};

/** referralEnums tones → the StatusChip palette above. */
export const chipTone = (tone) =>
  ({
    info: "sky",
    warning: "amber",
    success: "emerald",
    danger: "rose",
    default: "neutral",
  }[tone] || "neutral");

/** Shared loading placeholder; announced so a test (and a screen reader) can see the wait. */
export const LoadingPanel = ({ label = "Loading…" }) => (
  <div
    role="status"
    className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500"
  >
    {label}
  </div>
);

export const ErrorPanel = ({ title = "We couldn't load this", description, action }) => (
  <div className="rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-8 text-center">
    <p className="text-sm font-semibold text-rose-900 m-0">{title}</p>
    {description ? <p className="text-sm text-rose-700 mt-1 mb-0">{description}</p> : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);
